import React from 'react';
import { TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { partial } from 'lodash';
import RubricBlock from '@dyl/blocks/RubricBlock';
import ChipList from '@dyl/components/ChipList';
import ImagePicker from '@dyl/components/ImagePicker';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import withProps from '@dyl/utils/withProps';
import TaskItems from '@dyl/blocks/TaskItems';

export default withProps(TaskItems, { itemComponent: Item });

const useItemStyles = makeStyles(theme => ({
    image: {
        marginBottom: theme.spacing(2),
    },
    field: {
        // Default is inline-flex, but we want them on their own lines
        display: 'flex',
        marginBottom: theme.spacing(2),
        maxWidth: theme.breakpoints.values.sm,
    },
}));

function Item({ index }) {
    const classes = useItemStyles();
    const useField = partial(useTaskField, 'items', index);

    const [imageId, setImageId, imageError] = useField('image_id');

    const [optionsString, setOptionsString, optionsError] = useField('answers', 0, 'stimulus');
    const onOptionsChange = useChangeEvent(setOptionsString);

    const [answers, setAnswers, answersError] = useField('answers', 0, 'key');

    const [placeholder, setPlaceholder, placeholderError] = useField('answers', 0, 'fill_text');
    const onPlaceholderChange = useChangeEvent(setPlaceholder);

    return (
        <>
            <Typography variant='h6'>
                {index === 0 ? 'Example' : `Item ${index}`}
                {' - '}
                {index < 3 ? 'Fill Gap' : 'Type Whole Word'}
            </Typography>

            {index === 0 && <RubricBlock />}

            <ImagePicker
                label='Stimulus Image'
                value={imageId}
                onChange={id => setImageId(id)}
                fullWidth
                error={!!imageError}
                helperText={imageError || ' '}
                className={classes.image}
            />

            <ChipList
                label='Correct Answer(s)'
                helperText={answersError || 'The allowed correct answer(s) which the user should type one of.'}
                error={!!answersError}
                value={answers}
                onChange={setAnswers}
                // Example only allows one.
                {...(index === 0 && { max: 1 })}
                className={classes.field}
                {...(index > 3 && { isSpellingOrCSPreA1: true })}
            />

            {index < 3 && (
                <TextField
                    label='Incomplete Answer'
                    helperText={placeholderError || 'Use an underscore for each missing letter in the key. E.g. for "ship", with two missing letters you might type "__ip". Where one letter is missing you might type "_hip".'}
                    error={!!placeholderError}
                    value={placeholder ?? ''}
                    onChange={onPlaceholderChange}
                    variant='filled'
                    fullWidth
                    className={classes.field}
                />
            )}

            <TextField
                label='Keyboard Options'
                helperText={optionsError || 'Enter the options to be shown on the limited keyboard, separated by commas. The order you input will be reflected in the way letters are displayed in task.'}
                error={!!optionsError}
                value={optionsString ?? ''}
                onChange={onOptionsChange}
                variant='filled'
                fullWidth
                className={classes.field}
            />
        </>
    );
}